import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import BrandProfileWizard from '../BrandProfileWizard/BrandProfileWizard'

/**
 * Brand Profile Section Component
 *
 * Replaces the old additional_instructions textarea with a guided wizard.
 * Shows profile status and allows opening the wizard to configure/update.
 */
function BrandProfileSection({ accountId, onProfileUpdate }) {
  const { t } = useTranslation('settings')
  const [showWizard, setShowWizard] = useState(false)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load brand profile status
  const loadProfile = useCallback(async () => {
    if (!accountId) return

    try {
      setLoading(true)
      const data = await api.getBrandProfile(accountId)
      setProfile(data)
    } catch (error) {
      console.error('Failed to load brand profile:', error)
      setProfile({ status: 'error', error: error.message })
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  // Handle wizard completion
  const handleWizardComplete = useCallback((result) => {
    setShowWizard(false)
    loadProfile() // Refresh the profile
    onProfileUpdate?.(result)
  }, [loadProfile, onProfileUpdate])

  // Handle wizard cancel
  const handleWizardCancel = useCallback(() => {
    setShowWizard(false)
  }, [])

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      not_configured: {
        color: 'bg-gray-100 text-gray-600',
        icon: '○',
        label: t('brandProfile.status.notConfigured')
      },
      processing: {
        color: 'bg-yellow-100 text-yellow-700',
        icon: '◐',
        label: t('brandProfile.status.processing')
      },
      completed: {
        color: 'bg-green-100 text-green-700',
        icon: '●',
        label: t('brandProfile.status.completed')
      },
      error: {
        color: 'bg-red-100 text-red-700',
        icon: '✕',
        label: t('brandProfile.status.error')
      }
    }

    const config = statusConfig[status] || statusConfig.not_configured

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{t('brandProfile.title')}</h2>
        <p className="text-gray-600 mt-1">
          {t('brandProfile.description')}
        </p>
      </div>

      {/* Profile Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('brandProfile.cardTitle')}</h3>
              <p className="text-sm text-gray-500">{t('brandProfile.cardSubtitle')}</p>
            </div>
          </div>
          {!loading && <StatusBadge status={profile?.status} />}
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : profile?.status === 'completed' ? (
            <div className="space-y-4">
              {/* Brand Context - Full text */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {profile.brand_context}
                </p>
              </div>

              {/* Last Updated */}
              {profile.processed_at && (
                <p className="text-xs text-gray-500">
                  {t('brandProfile.lastUpdated')}: {new Date(profile.processed_at).toLocaleDateString()}
                </p>
              )}

              {/* Edit Button */}
              <button
                onClick={() => setShowWizard(true)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {t('brandProfile.editButton')}
              </button>
            </div>
          ) : profile?.status === 'processing' ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-sm text-gray-600">{t('brandProfile.processingMessage')}</p>
              <button
                onClick={loadProfile}
                className="mt-4 text-sm text-primary-600 hover:text-primary-700"
              >
                {t('brandProfile.refreshStatus')}
              </button>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <p className="text-gray-600 mb-4">{t('brandProfile.emptyMessage')}</p>
              <button
                onClick={() => setShowWizard(true)}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('brandProfile.configureButton')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Benefits Info */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm">
            <p className="font-medium text-primary-900">{t('brandProfile.benefitsTitle')}</p>
            <ul className="mt-2 text-primary-800 space-y-1">
              <li>• {t('brandProfile.benefit1')}</li>
              <li>• {t('brandProfile.benefit2')}</li>
              <li>• {t('brandProfile.benefit3')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <BrandProfileWizard
          accountId={accountId}
          existingProfile={profile}
          onComplete={handleWizardComplete}
          onCancel={handleWizardCancel}
        />
      )}
    </div>
  )
}

export default BrandProfileSection
